type RegistryGuestForExport = {
  name: string;
  phone: string;
  category: string;
  status: string;
  notes: string;
  latitude: number | null;
  longitude: number | null;
};

type RegistryTableText = {
  no: string;
  name: string;
  contact: string;
  category: string;
  status: string;
  notes: string;
  latitude: string;
  longitude: string;
  action: string;
};

const escapeHtml = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

export const filterRegistryGuestsByQuery = <T extends RegistryGuestForExport>(
  guests: T[],
  query: string,
) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return guests;
  }

  return guests.filter(
    (guest) =>
      guest.name.toLowerCase().includes(normalizedQuery) ||
      guest.phone.toLowerCase().includes(normalizedQuery) ||
      guest.category.toLowerCase().includes(normalizedQuery) ||
      guest.notes.toLowerCase().includes(normalizedQuery),
  );
};

export const downloadRegistryExcelFile = ({
  guests,
  language,
  tableText,
  selectedRegistryGroup,
}: {
  guests: RegistryGuestForExport[];
  language: "en" | "kh";
  tableText: RegistryTableText;
  selectedRegistryGroup: string;
}) => {
  if (guests.length === 0) {
    return;
  }

  const includeActionColumn = language === "kh";
  const rows = guests
    .map((guest, index) => {
      const cells = [
        index + 1,
        guest.name || "",
        guest.phone || "",
        guest.category || "",
        guest.status || "",
        guest.notes || "",
        guest.latitude != null ? guest.latitude.toFixed(6) : "",
        guest.longitude != null ? guest.longitude.toFixed(6) : "",
      ];
      if (includeActionColumn) {
        cells.push("-");
      }
      return cells.map((cell) => `<td>${escapeHtml(String(cell))}</td>`).join("");
    })
    .map((cells) => `<tr>${cells}</tr>`)
    .join("");

  const headers: string[] = [
    tableText.no,
    tableText.name,
    tableText.contact,
    tableText.category,
    tableText.status,
    tableText.notes,
    tableText.latitude,
    tableText.longitude,
  ];

  if (includeActionColumn) {
    headers.push(tableText.action);
  }

  const headerRow = headers.map((header) => `<th>${escapeHtml(header)}</th>`).join("");
  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
</head>
<body>
  <table border="1">
    <thead>
      <tr>${headerRow}</tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
</body>
</html>`;

  const blob = new Blob([html], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `guest-registry-${selectedRegistryGroup}-${new Date()
    .toISOString()
    .slice(0, 10)}.xls`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
